import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import PageTitle from "../../../components/common/PageTitle";
import AdminNavbar from "../../../components/layouts/AdminNavbar";
import { CenteredLayout } from "../../../components/layouts";
import ResourceAllocationSummaryList from "../../../components/layouts/ResourceAllocationSummaryList";
import useResourceAllocationSummary from "../../../hooks/useResourceAllocationSummary";
import { getCurrentCalendarPeriod, monthNames } from "../../../utils/consts";
import type { IResourceAllocationSummaryRowApi } from "../../../services/types/resourceAllocation";
import styles from "../../../components/layouts/EmplyeesListStyles";

function parsePeriodFromSearch(search: string) {
  const params = new URLSearchParams(search);
  const year = parseInt(params.get("year") ?? "", 10);
  const month = parseInt(params.get("month") ?? "", 10);
  const employeeId = params.get("employeeId") ?? "";

  if (Number.isFinite(year) && Number.isFinite(month) && month >= 1 && month <= 12) {
    return { month: monthNames[month - 1], year, employeeId };
  }

  return null;
}

const ResourceAllocation = () => {
  const history = useHistory();
  const location = useLocation();

  const periodFromUrl = useMemo(
    () => parsePeriodFromSearch(location.search) ?? { ...getCurrentCalendarPeriod(), employeeId: "" },
    [location.search]
  );

  const [{ month: selectedMonth, year: selectedYear, employeeId: selectedEmployeeId }, setPeriod] =
    useState(periodFromUrl);

  const setSelectedMonth = (month: string) =>
    setPeriod((prev) => ({ ...prev, month }));
  const setSelectedYear = (year: number) =>
    setPeriod((prev) => ({ ...prev, year }));
  const setSelectedEmployeeId = (employeeId: string) =>
    setPeriod((prev) => ({ ...prev, employeeId }));

  const {
    rows,
    filterEmployees,
    isLoading,
    readOnlyMode,
    acceptSettlement,
    rejectSettlement,
    monthIndex,
  } = useResourceAllocationSummary(selectedMonth, selectedYear, selectedEmployeeId);

  const monthNumber = monthIndex >= 0 ? monthIndex + 1 : monthNames.indexOf(selectedMonth) + 1;

  useEffect(() => {
    setPeriod((prev) => {
      if (
        prev.month === periodFromUrl.month
        && prev.year === periodFromUrl.year
        && prev.employeeId === periodFromUrl.employeeId
      ) {
        return prev;
      }
      return periodFromUrl;
    });
  }, [periodFromUrl]);

  useEffect(() => {
    if (monthNumber < 1) return;

    const urlPeriod = parsePeriodFromSearch(location.search);
    if (
      urlPeriod?.year === selectedYear
      && monthNames.indexOf(urlPeriod.month) + 1 === monthNumber
      && urlPeriod.employeeId === selectedEmployeeId
    ) {
      return;
    }

    const employeeQuery = selectedEmployeeId
      ? `&employeeId=${encodeURIComponent(selectedEmployeeId)}`
      : "";
    history.replace(`/resource-allocation?year=${selectedYear}&month=${monthNumber}${employeeQuery}`);
  }, [selectedMonth, selectedYear, selectedEmployeeId, monthNumber, history, location.search]);

  const handleRowClick = (row: IResourceAllocationSummaryRowApi) => {
    const employeeQuery = selectedEmployeeId
      ? `&employeeId=${encodeURIComponent(selectedEmployeeId)}`
      : "";
    history.push({
      pathname: `/resource-allocation/${row.employeeId}`,
      search: `?year=${selectedYear}&month=${monthNumber}${employeeQuery}`,
      state: {
        employeeName: `${row.lastName} ${row.firstName}`,
      },
    });
  };

  return (
    <>
      <AdminNavbar />
      <CenteredLayout>
        <PageTitle title="Alokacja zasobów" />
        <div className="table-container" style={styles.resourceAllocationPageContainer}>
          <ResourceAllocationSummaryList
            rows={rows}
            isLoading={isLoading}
            readOnlyMode={readOnlyMode}
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
            setSelectedMonth={setSelectedMonth}
            setSelectedYear={setSelectedYear}
            filterEmployees={filterEmployees}
            selectedEmployeeId={selectedEmployeeId}
            setSelectedEmployeeId={setSelectedEmployeeId}
            acceptSettlement={acceptSettlement}
            rejectSettlement={rejectSettlement}
            onRowClick={handleRowClick}
          />
        </div>
      </CenteredLayout>
    </>
  );
};

export default ResourceAllocation;
